import React, { useRef, useEffect, useState } from 'react';

interface PreauditAnswerCardProps {
  answer: any;
  isSelectedCandidate: boolean;
  isApproved: boolean;
  selectedValue: string;
  isOpen: boolean;
  showTrigger: boolean;
  onToggleOpen: (open: boolean) => void;
  onSelect: (value: string) => void;
  onClearClick: (e: React.MouseEvent) => void;
  onRevokeClick: (e: React.MouseEvent) => void;
}

const PreauditAnswerCard: React.FC<PreauditAnswerCardProps> = ({ 
  answer, 
  isSelectedCandidate, 
  isApproved, 
  selectedValue, 
  isOpen,
  showTrigger,
  onToggleOpen,
  onSelect,
  onClearClick,
  onRevokeClick
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLHeadingElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        setIsOverflowing(textRef.current.scrollHeight > textRef.current.clientHeight);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    
    // Timeout fallback to ensure styles/fonts are fully applied
    const timeoutId = setTimeout(checkOverflow, 150);

    return () => {
      window.removeEventListener('resize', checkOverflow);
      clearTimeout(timeoutId);
    };
  }, [answer.title]);

  const options = [
    { value: 'Approved Version', colorClass: 'cg-color-approved', borderColor: '#16a34a' },
    { value: 'Choose appropriate', colorClass: 'cg-color-default', borderColor: '#cbd5e1' }
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        if (isOpen) onToggleOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const currentDisplayValue = isSelectedCandidate || isApproved ? selectedValue : 'Choose appropriate';
  const selectedOption = options.find(opt => opt.value === currentDisplayValue) || options[1];

  return (
    <div className={`pa-card ${isSelectedCandidate ? 'selected' : ''} ${isApproved ? 'approved' : ''}`}>
      {showTrigger && (
        <div className="cg-card-label-dropdown" ref={dropdownRef}>
          <div 
            className={`cg-dropdown-trigger ${selectedOption.colorClass}`} 
            onClick={() => {
              if (!isApproved && !isSelectedCandidate) onToggleOpen(!isOpen);
            }}
          >
            {currentDisplayValue}
            {!isApproved && !isSelectedCandidate && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '4px' }}>
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            )}
            {!isApproved && isSelectedCandidate && (
              <svg onClick={onClearClick} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '6px', cursor: 'pointer' }}>
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            )}
            {isApproved && (
              <svg onClick={onRevokeClick} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '6px', cursor: 'pointer' }}>
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            )}
          </div>
          
          {isOpen && !isApproved && (
            <div className="cg-dropdown-menu">
            {options.map((opt) => (
              <div 
                key={opt.value}
                className={`cg-dropdown-item ${currentDisplayValue === opt.value ? 'selected' : ''}`}
                onClick={() => {
                  onSelect(opt.value);
                  onToggleOpen(false);
                }}
              >
                <div className="cg-dropdown-item-check">
                  {currentDisplayValue === opt.value && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  )}
                </div>
                <span className={`cg-dropdown-pill ${opt.colorClass}`}>{opt.value}</span>
              </div>
            ))}
          </div>
        )}
        </div>
      )}
      
      <div className="pa-card-title-container">
        <h4 
          ref={textRef} 
          className={`pa-card-title ${!isExpanded ? 'truncated-text' : ''}`}
        >
          {answer.title}
        </h4>
        {isOverflowing && (
          <div 
            className="pa-show-more-btn"
            onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </div>
        )}
      </div>
      <div className="pa-card-docname">{answer.documentName}</div>
      <div className="pa-card-snippet">{answer.snippet}</div>
    </div>
  );
};

export default PreauditAnswerCard;
